/**
 * WT-5 (16 Oct 2026 walk-through): where a sign-in or sign-up lands next.
 *
 * UC-B5 carries the return path as router state (`navigate('/login', { state:
 * { from } })`), which only in-app links can set. The new-account journey
 * crosses a page load, though: the confirm-email link opens from the inbox, so
 * Register also leaves the path here (this device only, tied to the new
 * account, kept for the confirm link's own 7 days) and ConfirmEmailPage takes
 * it on "Continue". Every path is checked again on the way out.
 */

const STASH_KEY = 'marakin_return_after_confirm';
const STASH_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // B-65: a confirm link lasts 7 days

/**
 * Same-site relative paths only: starts with "/" but not "//" (or "/\",
 * which browsers read as "//"), no control characters, sensible length.
 * Anything else → null, and the caller falls back to its usual landing.
 */
export function safeReturnPath(path) {
  if (typeof path !== 'string' || path.length > 512) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F\\]/.test(path)) return null;
  return path;
}

function defaultStore() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

/** Register: keep `path` for this account's confirm link. */
export function rememberReturnPath(path, userId, store = defaultStore()) {
  const safe = safeReturnPath(path);
  if (!safe || !userId || !store) return;
  try { store.setItem(STASH_KEY, JSON.stringify({ path: safe, userId, at: Date.now() })); } catch { /* storage unavailable */ }
}

/** Confirm page: the kept path for this account (once), or null. */
export function takeReturnPath(userId, store = defaultStore(), now = Date.now()) {
  if (!store) return null;
  let saved = null;
  try {
    saved = JSON.parse(store.getItem(STASH_KEY) || 'null');
    store.removeItem(STASH_KEY);
  } catch { return null; }
  if (!saved || !userId || saved.userId !== userId) return null;
  if (!(typeof saved.at === 'number' && now - saved.at >= 0 && now - saved.at < STASH_MAX_AGE_MS)) return null;
  return safeReturnPath(saved.path);
}
